var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Stack } from "@fluentui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../../styles/EscalationForm.css";
import { AZURE_FLOW_CALL_TYPES, CALL_TYPE_OPTIONS, callTypeRequiresIncident, callTypeRequiresSupportRequest, } from "./ConfidenceCallTypeConstants";
import { mainContainerStyle, stackTokens } from "./styles/shared.styles";
import { CallTypeSection } from "./components/CallTypeSection";
import { ContextRationaleSection } from "./components/ContextRationaleSection";
import { CustomerInformationSection } from "./components/CustomerInformationSection";
import { ProductComponentsSection, createProductRow, } from "./components/ProductComponentsSection";
import { RecentOutagesSection } from "./components/RecentOutagesSection";
import AzureSubscriptionSelector from "./AzureSubscriptionSelector";
import { getSubscriptionCandidates, } from "./actions/subscriptionCandidateActions";
import { normalizeAzureSubscriptionGuid, OTHER_SUBSCRIPTION_OPTION, } from "./subscriptionGuid";
import { useCommercialContextState, useCommercialContextDispatch, } from "../../reducers/CommercialContext/CommercialContextProvider";
import { getCustomerDataCollection } from "../../services/customersearch";
import { isValidCustomerTpid } from "./customerTpid";
import { findCountryMetadata } from "./countryMetadata";
import { getSalesIdCandidates } from "./salesIdCandidates";
const SALES_ID_LOOKUP_TIMEOUT_MS = 30000;
const withTimeout = (promise, timeoutMs) => new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error("MS Sales ID lookup timed out")), timeoutMs);
    promise.then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
    }, (error) => {
        window.clearTimeout(timeoutId);
        reject(error);
    });
});
// Removes the leading "<Parent> - " prefix from a catalogue product name for
// display only (e.g. "Azure - Virtual Machines" -> "Virtual Machines"). The
// full RM2 product name remains the option key and the submitted value.
// Mirrors the separator variants the Technical Product Issues tile handles.
const stripParentProductPrefix = (productName, parentProductName) => {
    const prefixes = [
        `${parentProductName} - `,
        `${parentProductName}  - `,
        `${parentProductName} – `,
    ];
    for (const prefix of prefixes) {
        if (productName.startsWith(prefix)) {
            return productName.slice(prefix.length);
        }
    }
    return productName;
};
export const ConfidenceCallTypeTab = ({ callTypeNextClicked, moveToNextTab, onCustomerDataLoaded, savedData, expandAllSections = false, }) => {
    var _a, _b, _c, _d, _e, _f;
    const [isTabInitialized, setIsTabInitialized] = useState(true);
    const [isValid, setIsValid] = useState(false);
    // Customer information state
    const [customerTpid, setCustomerTpid] = useState("");
    const [customerName, setCustomerName] = useState("");
    const [customerCountry, setCustomerCountry] = useState("");
    const [customerCountryId, setCustomerCountryId] = useState("");
    const [customerCountryAlpha2, setCustomerCountryAlpha2] = useState("");
    const [customerCountryAlpha3, setCustomerCountryAlpha3] = useState("");
    const [customerCountryIsEU, setCustomerCountryIsEU] = useState(false);
    const [customerMsSalesId, setCustomerMsSalesId] = useState("");
    const [salesIdCandidates, setSalesIdCandidates] = useState([]);
    const [salesIdLookupStatus, setSalesIdLookupStatus] = useState("idle");
    const [salesIdContextTpid, setSalesIdContextTpid] = useState("");
    const [preserveRestoredLayoutUntilSalesIdValidated, setPreserveRestoredLayoutUntilSalesIdValidated,] = useState(() => {
        var _a, _b;
        return !!savedData &&
            !savedData.isManualCustomerEntry &&
            isValidCustomerTpid((_a = savedData.customerTpid) !== null && _a !== void 0 ? _a : "") &&
            !!((_b = savedData.customerName) === null || _b === void 0 ? void 0 : _b.trim());
    });
    const salesIdRequestVersionRef = useRef(0);
    const hasAppliedSavedSalesIdRef = useRef(false);
    const hasEnteredSavedSalesIdContextRef = useRef(false);
    const [customerSegment, setCustomerSegment] = useState("");
    const [isManualCustomerEntry, setIsManualCustomerEntry] = useState(false);
    const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
    const [validCustomerTpid, setValidCustomerTpid] = useState(undefined);
    const [validCustomerName, setValidCustomerName] = useState(undefined);
    const [validCustomerCountry, setValidCustomerCountry] = useState(undefined);
    // Outages section state
    const [showOutagesSection, setShowOutagesSection] = useState(false);
    const [isOutagesSectionExpanded, setIsOutagesSectionExpanded] = useState(true);
    const [incidentCount, setIncidentCount] = useState(0);
    const [selectedIncidents, setSelectedIncidents] = useState([]);
    const [selectedSupportRequestNumbers, setSelectedSupportRequestNumbers] = useState([]);
    const [incidentDetails, setIncidentDetails] = useState([]); // Store full incident data for context
    const [supportTicketDetails, setSupportTicketDetails] = useState([]); // Loaded support tickets, used to map selected case numbers to titles
    const [subscriptionCandidates, setSubscriptionCandidates] = useState([]);
    const [subscriptionSelection, setSubscriptionSelection] = useState(undefined);
    const [manualAzureSubscriptionId, setManualAzureSubscriptionId] = useState("");
    const [azureSubscriptionId, setAzureSubscriptionId] = useState("");
    const [subscriptionLookupStatus, setSubscriptionLookupStatus] = useState("idle");
    const subscriptionRequestVersionRef = useRef(0);
    const subscriptionSelectionRef = useRef(undefined);
    const hasAppliedSavedSubscriptionRef = useRef(false);
    const initialSavedDataRef = useRef(savedData);
    const hasRestoredSavedDataRef = useRef(false);
    const [selectedTechnicalDepth, setSelectedTechnicalDepth] = useState("");
    const [selectedCallType, setSelectedCallType] = useState("");
    const [escalationReasonText, setEscalationReasonText] = useState("");
    const [desiredOutcome, setDesiredOutcome] = useState("");
    const [isDesiredOutcomeSelectionValid, setIsDesiredOutcomeSelectionValid,] = useState(true);
    const [businessImpact, setBusinessImpact] = useState("");
    const [stakeholders, setStakeholders] = useState([]);
    // Call Type section state
    const [isCallTypeSectionExpanded, setIsCallTypeSectionExpanded] = useState(true);
    // Context & Rationale state
    const [isContextSectionExpanded, setIsContextSectionExpanded] = useState(true);
    // Manual interaction tracking - prevents auto-close once user manually reopens
    const [hasManuallyReopenedCallType, setHasManuallyReopenedCallType] = useState(false);
    // Section unlock state - tracks which sections are enabled for interaction
    const [sectionsUnlocked, setSectionsUnlocked] = useState({
        callType: false,
        outages: false,
        context: false,
        productComponent: false,
    });
    const [isOptionalSectionExpanded, setIsOptionalSectionExpanded] = useState(false);
    // URL parameters state
    const [urlOutageIds, setUrlOutageIds] = useState([]);
    const previousManualModeRef = useRef(null);
    // Tracks the last customer identity (TPID | name) so incident/support-request
    // selections can be cleared when the user switches to a different customer.
    const previousCustomerIdentityRef = useRef(null);
    // Set while restoring saved data so the customer-change reset effect does not
    // wipe the restored selections when it observes the restored identity.
    const skipCustomerResetRef = useRef(false);
    // Set while restoring saved data that switches into manual-entry mode so the
    // mode-toggle reset effect does not wipe the restored selections.
    const skipModeResetRef = useRef(false);
    const selectedIncidentOutageMap = useMemo(() => {
        const map = {};
        selectedIncidents.forEach((incidentId) => {
            const incident = incidentDetails.find((item) => item.icmNumber === incidentId);
            if (incident) {
                map[incidentId] = !!incident.isOutage;
                return;
            }
            const parsedUrlIncident = urlOutageIds.find((item) => item.fullId === incidentId);
            if (parsedUrlIncident) {
                map[incidentId] = parsedUrlIncident.type.toUpperCase() === "OUTAGE";
                return;
            }
            map[incidentId] = false;
        });
        return map;
    }, [selectedIncidents, incidentDetails, urlOutageIds]);
    // Maps each selected support request number to its ticket title so the
    // submission payload can surface a human-readable reference description.
    // Built from the support tickets loaded for the customer, keyed by case number.
    const supportTicketTitleMap = useMemo(() => {
        return supportTicketDetails.reduce((map, ticket) => {
            if (selectedSupportRequestNumbers.includes(ticket.caseNumber)) {
                map[ticket.caseNumber] = ticket.title;
            }
            return map;
        }, {});
    }, [supportTicketDetails, selectedSupportRequestNumbers]);
    const subscriptionIncidentIds = useMemo(() => selectedIncidents.map((incidentId) => {
        var _a, _b;
        const trimmedId = incidentId.trim();
        const parsedId = (_a = trimmedId.match(/^(?:inc|icm)(\d+)$/i)) !== null && _a !== void 0 ? _a : trimmedId.match(/(?:^|[-:])(\d+)$/);
        return Number((_b = parsedId === null || parsedId === void 0 ? void 0 : parsedId[1]) !== null && _b !== void 0 ? _b : trimmedId);
    }), [selectedIncidents]);
    const subscriptionSupportRequestIds = useMemo(() => selectedSupportRequestNumbers
        .map((supportRequestId) => supportRequestId.trim())
        .filter((supportRequestId) => supportRequestId !== ""), [selectedSupportRequestNumbers]);
    const subscriptionContextKey = useMemo(() => [
        customerTpid.trim().toLowerCase(),
        [...subscriptionIncidentIds].sort().join(","),
        [...subscriptionSupportRequestIds]
            .map((supportRequestId) => supportRequestId.toLowerCase())
            .sort()
            .join(","),
    ].join("|"), [
        customerTpid,
        subscriptionIncidentIds,
        subscriptionSupportRequestIds,
    ]);
    const clearSubscriptionSelection = useCallback(() => {
        setSubscriptionSelection(undefined);
        setManualAzureSubscriptionId("");
        setAzureSubscriptionId("");
    }, []);
    const loadSalesIdCandidates = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _g, _h, _j, _k, _l, _m, _o;
        const requestVersion = salesIdRequestVersionRef.current + 1;
        salesIdRequestVersionRef.current = requestVersion;
        setCustomerMsSalesId("");
        setSalesIdCandidates([]);
        setSalesIdContextTpid("");
        const normalizedTpid = customerTpid.trim();
        const initialSavedData = initialSavedDataRef.current;
        const initialSavedTpid = (_h = (_g = initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.customerTpid) === null || _g === void 0 ? void 0 : _g.trim()) !== null && _h !== void 0 ? _h : "";
        const initialSavedManualMode = (_j = initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.isManualCustomerEntry) !== null && _j !== void 0 ? _j : (!initialSavedTpid && !!((_k = initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.customerName) === null || _k === void 0 ? void 0 : _k.trim()));
        const isInitialSavedSalesIdContext = normalizedTpid === initialSavedTpid &&
            isManualCustomerEntry === initialSavedManualMode;
        if (hasEnteredSavedSalesIdContextRef.current &&
            !isInitialSavedSalesIdContext) {
            setPreserveRestoredLayoutUntilSalesIdValidated(false);
        }
        if (!hasAppliedSavedSalesIdRef.current) {
            if (isInitialSavedSalesIdContext &&
                isValidCustomerTpid(normalizedTpid) &&
                customerName.trim()) {
                hasEnteredSavedSalesIdContextRef.current = true;
            }
            else if (hasEnteredSavedSalesIdContextRef.current &&
                !isInitialSavedSalesIdContext) {
                hasAppliedSavedSalesIdRef.current = true;
            }
        }
        if (isManualCustomerEntry ||
            !isValidCustomerTpid(normalizedTpid) ||
            !customerName.trim()) {
            setSalesIdLookupStatus("idle");
            return;
        }
        setSalesIdLookupStatus("loading");
        try {
            const customerData = yield withTimeout(getCustomerDataCollection({
                IsTop200SalesAccountsWithoutSearch: false,
                IsAllBySearchCriteria: true,
                TPId: normalizedTpid,
            }), SALES_ID_LOOKUP_TIMEOUT_MS);
            const rows = Array.isArray(customerData) ? customerData : [];
            if (requestVersion !== salesIdRequestVersionRef.current) {
                return;
            }
            const candidates = getSalesIdCandidates(rows, normalizedTpid);
            const hasSelectedTpidRows = rows.some((customer) => { var _a; return ((_a = customer.tpid) === null || _a === void 0 ? void 0 : _a.trim()) === normalizedTpid; });
            if (rows.length > 0 && !hasSelectedTpidRows) {
                setSalesIdLookupStatus("error");
                return;
            }
            setSalesIdCandidates(candidates);
            setSalesIdContextTpid(normalizedTpid);
            setSalesIdLookupStatus(candidates.length > 0 ? "success" : "empty");
            const savedSalesId = (_m = (_l = initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.customerMsSalesId) === null || _l === void 0 ? void 0 : _l.trim()) !== null && _m !== void 0 ? _m : "";
            const canRestoreSavedSalesId = !hasAppliedSavedSalesIdRef.current &&
                ((_o = initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.customerTpid) === null || _o === void 0 ? void 0 : _o.trim()) === normalizedTpid &&
                candidates.includes(savedSalesId);
            hasAppliedSavedSalesIdRef.current = true;
            if (canRestoreSavedSalesId) {
                setCustomerMsSalesId(savedSalesId);
            }
            setPreserveRestoredLayoutUntilSalesIdValidated(false);
        }
        catch (_p) {
            if (requestVersion !== salesIdRequestVersionRef.current) {
                return;
            }
            setSalesIdLookupStatus("error");
        }
    }), [customerName, customerTpid, isManualCustomerEntry]);
    useEffect(() => {
        void loadSalesIdCandidates();
        return () => {
            salesIdRequestVersionRef.current += 1;
        };
    }, [loadSalesIdCandidates]);
    useEffect(() => {
        subscriptionSelectionRef.current = subscriptionSelection;
    }, [subscriptionSelection]);
    const isSalesIdSelectionSatisfied = salesIdContextTpid === customerTpid.trim() &&
        (salesIdLookupStatus === "empty" ||
            (salesIdLookupStatus === "success" &&
                salesIdCandidates.includes(customerMsSalesId)));
    const initialSavedTpid = (_c = (_b = (_a = initialSavedDataRef.current) === null || _a === void 0 ? void 0 : _a.customerTpid) === null || _b === void 0 ? void 0 : _b.trim()) !== null && _c !== void 0 ? _c : "";
    const shouldPreserveRestoredSalesIdLayout = preserveRestoredLayoutUntilSalesIdValidated &&
        !isManualCustomerEntry &&
        customerTpid.trim() === initialSavedTpid;
    const clearContextDerivedSubscriptionSelection = useCallback(() => {
        if (subscriptionSelectionRef.current !== OTHER_SUBSCRIPTION_OPTION) {
            clearSubscriptionSelection();
        }
    }, [clearSubscriptionSelection]);
    const loadSubscriptionCandidates = useCallback((preserveSelection) => __awaiter(void 0, void 0, void 0, function* () {
        const requestVersion = subscriptionRequestVersionRef.current + 1;
        subscriptionRequestVersionRef.current = requestVersion;
        if (!preserveSelection) {
            clearContextDerivedSubscriptionSelection();
        }
        setSubscriptionCandidates([]);
        if (!customerTpid.trim()) {
            setSubscriptionLookupStatus("error");
            return;
        }
        setSubscriptionLookupStatus("loading");
        try {
            const candidates = yield getSubscriptionCandidates(customerTpid, {
                incidentIds: subscriptionIncidentIds,
                supportRequestIds: subscriptionSupportRequestIds,
            }, customerCountryIsEU);
            if (requestVersion !== subscriptionRequestVersionRef.current) {
                return;
            }
            setSubscriptionCandidates(candidates);
            setSubscriptionLookupStatus(candidates.length > 0 ? "success" : "empty");
            if (preserveSelection &&
                subscriptionSelectionRef.current &&
                subscriptionSelectionRef.current !== OTHER_SUBSCRIPTION_OPTION &&
                !candidates.includes(subscriptionSelectionRef.current)) {
                clearSubscriptionSelection();
            }
        }
        catch (_q) {
            if (requestVersion !== subscriptionRequestVersionRef.current) {
                return;
            }
            setSubscriptionLookupStatus("error");
        }
    }), [
        clearSubscriptionSelection,
        clearContextDerivedSubscriptionSelection,
        customerCountryIsEU,
        customerTpid,
        subscriptionIncidentIds,
        subscriptionSupportRequestIds,
    ]);
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        if (selectedCallType !== "outage" ||
            subscriptionIncidentIds.length === 0) {
            subscriptionRequestVersionRef.current += 1;
            setSubscriptionCandidates([]);
            setSubscriptionLookupStatus("idle");
            clearSubscriptionSelection();
            return;
        }
        if (customerTpid.trim() === "") {
            subscriptionRequestVersionRef.current += 1;
            setSubscriptionCandidates([]);
            setSubscriptionLookupStatus("idle");
            return;
        }
        const initialSavedData = initialSavedDataRef.current;
        const canRestoreSavedSubscription = !hasAppliedSavedSubscriptionRef.current &&
            (initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.subscriptionContextKey) === subscriptionContextKey &&
            !!initialSavedData.azureSubscriptionId &&
            !!normalizeAzureSubscriptionGuid(initialSavedData.azureSubscriptionId);
        hasAppliedSavedSubscriptionRef.current = true;
        if (canRestoreSavedSubscription) {
            setSubscriptionCandidates((_a = initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.subscriptionCandidates) !== null && _a !== void 0 ? _a : []);
            setSubscriptionSelection(initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.azureSubscriptionSelection);
            setManualAzureSubscriptionId((_b = initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.manualAzureSubscriptionId) !== null && _b !== void 0 ? _b : "");
            setAzureSubscriptionId((_c = normalizeAzureSubscriptionGuid(initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.azureSubscriptionId)) !== null && _c !== void 0 ? _c : "");
            setSubscriptionLookupStatus(((_e = (_d = initialSavedData === null || initialSavedData === void 0 ? void 0 : initialSavedData.subscriptionCandidates) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0) > 0
                ? "success"
                : "empty");
            return;
        }
        void loadSubscriptionCandidates(false);
        return () => {
            subscriptionRequestVersionRef.current += 1;
        };
    }, [
        clearSubscriptionSelection,
        customerTpid,
        loadSubscriptionCandidates,
        selectedCallType,
        subscriptionContextKey,
        subscriptionIncidentIds.length,
    ]);
    // Products state
    const [isProductComponentExpanded, setIsProductComponentExpanded] = useState(false);
    const [customProductRows, setCustomProductRows] = useState(() => [createProductRow()]);
    // RM2 product catalogue for the Products dropdown, sourced live from
    // commercial metadata ([external].Submission_vProduct). RM2 stores products
    // through a foreign key to dbo.Product and EM-API rejects anything that is not
    // an exact, active RM2 ProductName, so the option KEY is always the full
    // catalogue name (e.g. "Azure - Cosmos DB"). Only the display text has the
    // parent prefix stripped. Scope is the Azure parent family. AB#39926822.
    const commercialState = useCommercialContextState();
    const commercialMetadata = (_d = commercialState === null || commercialState === void 0 ? void 0 : commercialState.CommercialContext) === null || _d === void 0 ? void 0 : _d.CommercialMetadata;
    // Loading/error state of the commercial-metadata fetch that backs the product
    // dropdown, plus a retry dispatch. Surfaced so the section can show an
    // explicit loading state, and an error with a retry action on a failed
    // request, rather than an indefinitely-empty disabled dropdown. AB#39245060.
    const azureComponentOptionsLoading = (_e = commercialState === null || commercialState === void 0 ? void 0 : commercialState.InProgress) !== null && _e !== void 0 ? _e : false;
    const azureComponentOptionsError = (_f = commercialState === null || commercialState === void 0 ? void 0 : commercialState.Error) !== null && _f !== void 0 ? _f : "";
    const { getCommercialMetadata } = useCommercialContextDispatch();
    const azureComponentOptions = useMemo(() => {
        var _a, _b, _c;
        const products = (_a = commercialMetadata === null || commercialMetadata === void 0 ? void 0 : commercialMetadata.products) !== null && _a !== void 0 ? _a : [];
        const seen = new Set();
        const candidates = [];
        for (const product of products) {
            const parentName = (_b = product.parentProductName) === null || _b === void 0 ? void 0 : _b.trim();
            const fullName = (_c = product.productName) === null || _c === void 0 ? void 0 : _c.trim();
            if (!parentName || !fullName || parentName.toLowerCase() !== "azure") {
                continue;
            }
            const dedupeKey = fullName.toLowerCase();
            if (seen.has(dedupeKey)) {
                continue;
            }
            seen.add(dedupeKey);
            candidates.push({
                key: fullName,
                displayName: stripParentProductPrefix(fullName, parentName),
                productId: product.productId,
            });
        }
        // Two distinct RM2 products can share a display name once the prefix is
        // stripped (e.g. "Azure - Virtual Machines" and "Virtual Machines"). Show
        // the full name for those so the user can tell them apart.
        const displayCounts = new Map();
        candidates.forEach(({ displayName }) => {
            var _a;
            const countKey = displayName.toLowerCase();
            displayCounts.set(countKey, ((_a = displayCounts.get(countKey)) !== null && _a !== void 0 ? _a : 0) + 1);
        });
        const options = candidates.map(({ key, displayName, productId }) => {
            var _a;
            return ({
                key,
                text: ((_a = displayCounts.get(displayName.toLowerCase())) !== null && _a !== void 0 ? _a : 0) > 1
                    ? key
                    : displayName,
                data: { productId },
            });
        });
        options.sort((a, b) => a.text.localeCompare(b.text));
        return options;
    }, [commercialMetadata]);
    // RM2 country codes come from the commercial metadata catalogue, which can
    // arrive after the customer has already been selected (deep link, restored
    // draft, or a slow metadata fetch). Re-resolve whenever either side changes
    // so the payload never ships an empty country.alpha3 — UEM rejects such a
    // submission with a 400 before it reaches RM2. The codes are used verbatim:
    // RM2 has non-ISO values (Kosovo is XKS) that cannot be derived from the
    // display name or from alpha2.
    useEffect(() => {
        const countryName = customerCountry.trim();
        const countries = commercialMetadata === null || commercialMetadata === void 0 ? void 0 : commercialMetadata.countries;
        if (!countryName || !(countries === null || countries === void 0 ? void 0 : countries.length)) {
            return;
        }
        const metadata = findCountryMetadata(countries, countryName);
        const nextCountryId = (metadata === null || metadata === void 0 ? void 0 : metadata.id) || "";
        const nextAlpha2 = (metadata === null || metadata === void 0 ? void 0 : metadata.alpha2) || "";
        const nextAlpha3 = (metadata === null || metadata === void 0 ? void 0 : metadata.alpha3) || "";
        const nextIsEU = Boolean(metadata === null || metadata === void 0 ? void 0 : metadata.isEU);
        // Clearing on a failed match is deliberate: carrying codes forward from a
        // previously selected country would submit the wrong country to RM2.
        if (nextCountryId !== customerCountryId) {
            setCustomerCountryId(nextCountryId);
        }
        if (nextAlpha2 !== customerCountryAlpha2) {
            setCustomerCountryAlpha2(nextAlpha2);
        }
        if (nextAlpha3 !== customerCountryAlpha3) {
            setCustomerCountryAlpha3(nextAlpha3);
        }
        if (nextIsEU !== customerCountryIsEU) {
            setCustomerCountryIsEU(nextIsEU);
        }
    }, [
        customerCountry,
        customerCountryId,
        customerCountryAlpha2,
        customerCountryAlpha3,
        customerCountryIsEU,
        commercialMetadata,
    ]);
    // Once the catalogue has loaded, normalise every product row to an exact
    // catalogue key. Drafts saved before AB#39926822 stored the prefix-stripped
    // display name (e.g. "Cosmos DB"), so a value that is not a key but uniquely
    // matches one option's display text is mapped to that option's full RM2
    // name. Anything else (outage- or support-request-derived names, free text)
    // is cleared so it can neither pass completion nor be submitted.
    const azureComponentOptionKeys = useMemo(() => new Set(azureComponentOptions.map((o) => String(o.key))), [azureComponentOptions]);
    useEffect(() => {
        if (azureComponentOptions.length === 0) {
            return;
        }
        const resolveRowValue = (value) => {
            const trimmed = value.trim();
            if (!trimmed || azureComponentOptionKeys.has(trimmed)) {
                return trimmed;
            }
            const lowered = trimmed.toLowerCase();
            const keyMatch = azureComponentOptions.find((option) => String(option.key).toLowerCase() === lowered);
            if (keyMatch) {
                return String(keyMatch.key);
            }
            const textMatches = azureComponentOptions.filter((option) => option.text.toLowerCase() === lowered);
            return textMatches.length === 1 ? String(textMatches[0].key) : "";
        };
        setCustomProductRows((prev) => {
            const next = prev.map((row) => {
                const value = resolveRowValue(row.value);
                return value === row.value ? row : Object.assign(Object.assign({}, row), { value });
            });
            const changed = next.some((row, i) => row !== prev[i]);
            return changed ? next : prev;
        });
    }, [azureComponentOptions, azureComponentOptionKeys, customProductRows]);
    // Function to parse outage ID from URL parameter
    const parseOutageId = (outageIdString) => {
        // Handle format like "IcM-697963810"
        const match = outageIdString.match(/^([A-Za-z]+)-?(.+)$/);
        if (match) {
            const [, type, id] = match;
            return {
                type: type.toUpperCase(),
                id: id,
                fullId: outageIdString,
            };
        }
        // Fallback if format doesn't match expected pattern
        return {
            type: "UNKNOWN",
            id: outageIdString,
            fullId: outageIdString,
        };
    };
    // Parse URL parameters on component mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tpid = urlParams.get("TPID");
        const customerName = urlParams.get("CustomerName");
        const customerCountry = urlParams.get("CustomerCountry");
        const callType = urlParams.get("CallType");
        const outageId = urlParams.get("OutageID");
        // Check if required parameters are present
        if (tpid && customerName && callType) {
            // Set customer information
            setCustomerTpid(tpid);
            setCustomerName(customerName);
            if (customerCountry) {
                setCustomerCountry(customerCountry);
            }
            setValidCustomerTpid(isValidCustomerTpid(tpid));
            // Hydrating the customer identity here will trip the customer-identity
            // reset effect (deps [customerTpid, customerName]) on the next commit,
            // which would wipe the incident selections we set below (selectedIncidents
            // / urlOutageIds / incidentCount) and permanently drop the deep-linked
            // mandatory incident (this mount-only effect never re-runs). Arm the skip
            // so that single hydration-driven identity change is absorbed, mirroring
            // the saved-data restore effect. tpid + customerName are guaranteed
            // non-empty here, so the identity always changes from the seeded value and
            // the flag is consumed exactly once (no stranding).
            skipCustomerResetRef.current = true;
            // Find and set call type. Only Azure-flow call types drive this tab; a
            // hidden (support/communication) or unknown deep-linked CallType is
            // ignored here so we never select a type whose incident-selection UI
            // stays hidden while callTypeRequiresIncident() reports it as required —
            // that combination would dead-end the user with a permanently disabled
            // Continue. Ignoring it leaves the call-type picker open for a valid choice.
            const matchingCallType = CALL_TYPE_OPTIONS.find((option) => option.key.toLowerCase() === callType.toLowerCase() &&
                AZURE_FLOW_CALL_TYPES.has(option.key));
            if (matchingCallType) {
                setSelectedCallType(matchingCallType.key);
                setShowOutagesSection(true);
            }
            // Handle outage IDs if provided
            if (outageId) {
                const rawOutageIds = outageId
                    .split(",")
                    .map((id) => id.trim())
                    .filter((id) => id);
                // Parse each outage ID and store both parsed data and original IDs
                const parsedOutages = rawOutageIds.map((id) => parseOutageId(id));
                setUrlOutageIds(parsedOutages);
                setSelectedIncidents(rawOutageIds); // Keep original format for selectedIncidents
                setIncidentCount(parsedOutages.length);
            }
            // Unlock sections. matchingCallType is now guaranteed to be an Azure-flow
            // type (or undefined), so the incident/outages section unlocks exactly
            // when a valid Azure call type was hydrated.
            setSectionsUnlocked({
                callType: true,
                outages: !!matchingCallType,
                context: true,
                productComponent: true,
            });
            // Set section visibility for URL loaded content. When no valid Azure-flow
            // call type was hydrated, keep the call-type picker expanded so the user
            // is prompted to choose one rather than facing a collapsed, required-yet-
            // incomplete section.
            setIsCallTypeSectionExpanded(!matchingCallType);
            setIsOutagesSectionExpanded(false); // Collapse outages
            setIsContextSectionExpanded(true); // Open context
            // Notify parent component about customer data
            if (onCustomerDataLoaded) {
                onCustomerDataLoaded(tpid, customerName, customerCountry || "", "", "");
            }
        }
    }, []); // Empty dependency array to run only on mount
    useEffect(() => {
        if (callTypeNextClicked > 0 && !isTabInitialized) {
            validateTab();
        }
    }, [callTypeNextClicked]);
    useEffect(() => {
        if (isTabInitialized) {
            setIsTabInitialized(false);
        }
    }, []);
    useEffect(() => {
        // Validate whenever customer data changes - require either valid TPID or customer name
        const hasTpid = customerTpid.trim() !== "";
        const hasCustomerName = customerName.trim() !== "";
        const isValidTpid = hasTpid && isValidCustomerTpid(customerTpid);
        const isValidName = hasCustomerName && customerName.trim().length >= 2;
        const isValidCountryForManual = !isManualCustomerEntry || customerCountry.trim() !== "";
        const isSalesIdValid = isManualCustomerEntry || isSalesIdSelectionSatisfied;
        const isCustomerValid = (isValidTpid || isValidName) &&
            isValidCountryForManual &&
            !isLoadingCustomer &&
            isSalesIdValid;
        setIsValid(isCustomerValid);
    }, [
        customerTpid,
        customerName,
        customerCountry,
        isManualCustomerEntry,
        isLoadingCustomer,
        salesIdCandidates,
        salesIdContextTpid,
        salesIdLookupStatus,
        customerMsSalesId,
    ]);
    // Restore saved data when coming back from review
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        if (savedData && !hasRestoredSavedDataRef.current) {
            hasRestoredSavedDataRef.current = true;
            // The restoration below sets customer identity and selections together;
            // absorb the resulting identity change so selections are not reset. Arm
            // only when the restored identity actually differs from the current one,
            // otherwise a re-run of this effect (savedData is a fresh object each
            // parent render) would strand the flag and wrongly skip the next genuine
            // customer change.
            const restoredIdentity = `${(_b = (_a = savedData.customerTpid) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : ""}|${(_d = (_c = savedData.customerName) === null || _c === void 0 ? void 0 : _c.trim()) !== null && _d !== void 0 ? _d : ""}`;
            const currentIdentity = `${customerTpid.trim()}|${customerName.trim()}`;
            const hasRestoredIdentity = !!(((_e = savedData.customerTpid) === null || _e === void 0 ? void 0 : _e.trim()) || ((_f = savedData.customerName) === null || _f === void 0 ? void 0 : _f.trim()));
            skipCustomerResetRef.current =
                hasRestoredIdentity && restoredIdentity !== currentIdentity;
            // Restoring into manual-entry mode flips isManualCustomerEntry from its
            // default (false) to true, which the mode-toggle reset effect would treat
            // as a user toggle and wipe the restored selections. Arm the skip only
            // when the restored mode will actually cause that change, otherwise a
            // re-run of this effect would strand the flag.
            const restoredManualMode = savedData.isManualCustomerEntry !== undefined
                ? savedData.isManualCustomerEntry
                : !((_g = savedData.customerTpid) === null || _g === void 0 ? void 0 : _g.trim()) && !!((_h = savedData.customerName) === null || _h === void 0 ? void 0 : _h.trim());
            skipModeResetRef.current =
                restoredManualMode === true && isManualCustomerEntry !== true;
            // Restore customer information
            if (savedData.customerTpid) {
                setCustomerTpid(savedData.customerTpid);
            }
            if (savedData.customerName) {
                setCustomerName(savedData.customerName);
                setValidCustomerTpid(true);
            }
            if (savedData.customerCountry) {
                setCustomerCountry(savedData.customerCountry);
            }
            if (savedData.customerCountryId) {
                setCustomerCountryId(savedData.customerCountryId);
            }
            if (savedData.customerCountryAlpha2) {
                setCustomerCountryAlpha2(savedData.customerCountryAlpha2);
            }
            if (savedData.customerCountryAlpha3) {
                setCustomerCountryAlpha3(savedData.customerCountryAlpha3);
            }
            if (savedData.customerCountryIsEU !== undefined) {
                setCustomerCountryIsEU(savedData.customerCountryIsEU);
            }
            if (savedData.customerSegment) {
                setCustomerSegment(savedData.customerSegment);
            }
            if (savedData.isManualCustomerEntry !== undefined) {
                setIsManualCustomerEntry(savedData.isManualCustomerEntry);
            }
            else {
                setIsManualCustomerEntry(!((_j = savedData.customerTpid) === null || _j === void 0 ? void 0 : _j.trim()) && !!((_k = savedData.customerName) === null || _k === void 0 ? void 0 : _k.trim()));
            }
            // Restore incidents
            if (savedData.selectedIncidents) {
                setSelectedIncidents(savedData.selectedIncidents);
                setShowOutagesSection(savedData.selectedIncidents.length > 0);
            }
            if (savedData.selectedSupportRequestNumbers) {
                setSelectedSupportRequestNumbers(savedData.selectedSupportRequestNumbers);
            }
            // Restore call type selection
            if (savedData.type || savedData.typeKey) {
                const callType = savedData.type || savedData.typeKey || "";
                if (AZURE_FLOW_CALL_TYPES.has(callType)) {
                    setSelectedCallType(callType);
                    // If outage is selected and customer is available, show outages section
                    const canShowOutagesFromSearch = !!((_l = savedData.customerTpid) === null || _l === void 0 ? void 0 : _l.trim()) && !!((_m = savedData.customerName) === null || _m === void 0 ? void 0 : _m.trim());
                    const canShowOutagesFromManual = !!savedData.isManualCustomerEntry &&
                        !!((_o = savedData.customerName) === null || _o === void 0 ? void 0 : _o.trim()) &&
                        !!((_p = savedData.customerCountry) === null || _p === void 0 ? void 0 : _p.trim());
                    if (canShowOutagesFromSearch || canShowOutagesFromManual) {
                        setShowOutagesSection(true);
                    }
                    // Unlock sections based on saved data
                    setSectionsUnlocked((prev) => (Object.assign(Object.assign({}, prev), { callType: true, context: true, outages: true, productComponent: true })));
                }
            }
            // Restore technical depth
            if (savedData.technicalDepth) {
                setSelectedTechnicalDepth(savedData.technicalDepth);
            }
            if (savedData.escalationReasons) {
                setEscalationReasonText(savedData.escalationReasons.join(", "));
            }
            // Restore text fields
            if (savedData.desiredOutcome)
                setDesiredOutcome(savedData.desiredOutcome);
            if (savedData.businessImpact)
                setBusinessImpact(savedData.businessImpact);
            if (savedData.stakeholders)
                setStakeholders(savedData.stakeholders);
            // Restore product rows from the saved selection. Values are normalised
            // against the RM2 catalogue once it loads (see the effect above), so a
            // legacy display name maps to its full RM2 name and anything that is not
            // in the catalogue is cleared rather than submitted.
            const savedProductNames = Array.from(new Set(((_q = savedData.selectedProducts) !== null && _q !== void 0 ? _q : [])
                .map((product) => { var _a; return (_a = product.name) === null || _a === void 0 ? void 0 : _a.trim(); })
                .filter((name) => !!name)));
            if (savedProductNames.length > 0) {
                setCustomProductRows(savedProductNames.map((name) => createProductRow(name)));
            }
        }
    }, [savedData]);
    // Unlock call type section for either:
    // 1) customer selected from search (valid TPID + name), or
    // 2) manual customer entry (name + mandatory customer location)
    useEffect(() => {
        const canUnlockFromSearch = isValidCustomerTpid(customerTpid) &&
            customerName.trim() !== "" &&
            (isSalesIdSelectionSatisfied || shouldPreserveRestoredSalesIdLayout);
        const canUnlockFromManual = isManualCustomerEntry &&
            customerName.trim() !== "" &&
            customerCountry.trim() !== "";
        setSectionsUnlocked((prev) => (Object.assign(Object.assign({}, prev), { callType: canUnlockFromSearch || canUnlockFromManual })));
    }, [
        customerTpid,
        customerName,
        isManualCustomerEntry,
        customerCountry,
        isSalesIdSelectionSatisfied,
        shouldPreserveRestoredSalesIdLayout,
    ]);
    useEffect(() => {
        const hasSearchCustomer = isValidCustomerTpid(customerTpid) &&
            customerName.trim() !== "" &&
            (isSalesIdSelectionSatisfied || shouldPreserveRestoredSalesIdLayout);
        const hasManualCustomer = isManualCustomerEntry &&
            customerName.trim() !== "" &&
            customerCountry.trim() !== "";
        const shouldEnableOutagesSection = AZURE_FLOW_CALL_TYPES.has(selectedCallType) &&
            (hasSearchCustomer || hasManualCustomer);
        setShowOutagesSection(shouldEnableOutagesSection);
        setSectionsUnlocked((prev) => (Object.assign(Object.assign({}, prev), { outages: shouldEnableOutagesSection })));
    }, [
        selectedCallType,
        customerTpid,
        customerName,
        customerCountry,
        isManualCustomerEntry,
        isSalesIdSelectionSatisfied,
        shouldPreserveRestoredSalesIdLayout,
    ]);
    useEffect(() => {
        // Skip reset on initial hydration/restoration; only react to user mode toggles.
        if (previousManualModeRef.current === null) {
            previousManualModeRef.current = isManualCustomerEntry;
            return;
        }
        if (previousManualModeRef.current === isManualCustomerEntry) {
            return;
        }
        previousManualModeRef.current = isManualCustomerEntry;
        // Absorb the mode change produced by restoring saved data into manual mode.
        if (skipModeResetRef.current) {
            skipModeResetRef.current = false;
            return;
        }
        // Switching between search/manual customer modes invalidates incident context.
        setSelectedIncidents([]);
        setSelectedSupportRequestNumbers([]);
        setSupportTicketDetails([]);
        setIncidentDetails([]);
        setIncidentCount(0);
        setUrlOutageIds([]);
        setShowOutagesSection(false);
        setIsOutagesSectionExpanded(true);
    }, [isManualCustomerEntry]);
    // Reset incident/support-request context whenever the customer identity
    // (TPID / name) changes, so selections made for one customer can never carry
    // into a different customer's submission. Mode toggles are handled above.
    useEffect(() => {
        var _a;
        const identity = `${customerTpid.trim()}|${customerName.trim()}`;
        // Seed on first run without resetting (covers initial mount/hydration).
        if (previousCustomerIdentityRef.current === null) {
            previousCustomerIdentityRef.current = identity;
            return;
        }
        if (previousCustomerIdentityRef.current === identity) {
            return;
        }
        // A TPID that arrives after the name (deep link, restored draft, or a
        // search result that resolves late) is hydration of the same customer, not
        // a switch to a different one. Resetting here would wipe the restored
        // incident/support-request context before the subscription can be restored.
        const previousTpid = (_a = previousCustomerIdentityRef.current.split("|")[0]) !== null && _a !== void 0 ? _a : "";
        const previousName = previousCustomerIdentityRef.current
            .slice(previousTpid.length + 1);
        const isTpidHydration = previousTpid === "" &&
            customerTpid.trim() !== "" &&
            previousName === customerName.trim();
        previousCustomerIdentityRef.current = identity;
        if (isTpidHydration) {
            return;
        }
        // Absorb the identity change produced by restoring saved data.
        if (skipCustomerResetRef.current) {
            skipCustomerResetRef.current = false;
            return;
        }
        setSelectedIncidents([]);
        setSelectedSupportRequestNumbers([]);
        setSupportTicketDetails([]);
        setIncidentDetails([]);
        setIncidentCount(0);
        setUrlOutageIds([]);
    }, [customerTpid, customerName]);
    // Expand only completed sections when coming back from review
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (expandAllSections && savedData) {
            // Expand sections that have data filled in
            const hasCallType = !!(savedData.type || savedData.typeKey);
            const hasIssueReferences = !!(((_a = savedData.selectedIncidents) === null || _a === void 0 ? void 0 : _a.length) ||
                ((_b = savedData.selectedSupportRequestNumbers) === null || _b === void 0 ? void 0 : _b.length));
            const hasContext = !!(((_c = savedData.technicalDepth) === null || _c === void 0 ? void 0 : _c.trim()) || ((_d = savedData.desiredOutcome) === null || _d === void 0 ? void 0 : _d.trim()) || ((_e = savedData.escalationReasons) === null || _e === void 0 ? void 0 : _e.length));
            const hasProducts = !!(((_f = savedData.selectedProducts) === null || _f === void 0 ? void 0 : _f.length) || ((_g = savedData.workloads) === null || _g === void 0 ? void 0 : _g.length));
            const hasOptionalContent = !!(((_h = savedData.businessImpact) === null || _h === void 0 ? void 0 : _h.trim()) || (savedData.stakeholders && savedData.stakeholders.length > 0));
            setIsCallTypeSectionExpanded(hasCallType);
            setIsOutagesSectionExpanded(hasIssueReferences);
            setIsContextSectionExpanded(hasContext);
            setIsProductComponentExpanded(hasProducts);
            setIsOptionalSectionExpanded(hasOptionalContent);
            // Show outages section if incidents were selected
            if (hasIssueReferences) {
                setShowOutagesSection(true);
            }
            // Prevent auto-collapse by marking as manually opened
            setHasManuallyReopenedCallType(true);
            // Unlock all sections when expanding from review
            setSectionsUnlocked({
                callType: true,
                outages: true,
                context: true,
                productComponent: true,
            });
        }
    }, [expandAllSections, savedData]);
    // Show Context & Rationale section when incidents are selected and Next is clicked
    // Note: The context section is now shown via handleOutagesNext, not automatically
    useEffect(() => {
        // Auto-collapse call type section when first incident is selected (only if not manually reopened)
        if ((selectedIncidents.length > 0 ||
            selectedSupportRequestNumbers.length > 0) &&
            !hasManuallyReopenedCallType) {
            setIsCallTypeSectionExpanded(false);
        }
    }, [
        selectedIncidents,
        selectedSupportRequestNumbers,
        hasManuallyReopenedCallType,
    ]);
    // Check if all required fields in Context & Rationale are complete
    const isContextSectionComplete = () => {
        const hasSelectedTechnicalDepth = selectedTechnicalDepth.trim() !== "";
        const hasEscalationReason = escalationReasonText.trim() !== "";
        const hasDesiredOutcome = desiredOutcome.trim() !== "" && isDesiredOutcomeSelectionValid;
        return (hasSelectedTechnicalDepth &&
            hasEscalationReason &&
            hasDesiredOutcome);
    };
    // Customer required-field check.
    // Search mode requires TPID + name; manual mode requires name + country.
    // The branches are mode-exclusive because manual entry populates customerTpid
    // from the company name, which would otherwise satisfy the search branch and
    // bypass the manual-mode country requirement.
    const isCustomerComplete = () => {
        const hasSearchCustomer = !isManualCustomerEntry &&
            isValidCustomerTpid(customerTpid) &&
            customerName.trim() !== "" &&
            isSalesIdSelectionSatisfied;
        const hasManualCustomer = isManualCustomerEntry &&
            customerName.trim() !== "" &&
            customerCountry.trim() !== "";
        return hasSearchCustomer || hasManualCustomer;
    };
    const isSubscriptionComplete = () => {
        if (selectedCallType !== "outage") {
            return true;
        }
        if (subscriptionLookupStatus === "idle" ||
            subscriptionLookupStatus === "loading") {
            return false;
        }
        if (subscriptionSelection === OTHER_SUBSCRIPTION_OPTION) {
            return (normalizeAzureSubscriptionGuid(manualAzureSubscriptionId) ===
                azureSubscriptionId);
        }
        return (!!subscriptionSelection &&
            subscriptionCandidates.includes(subscriptionSelection) &&
            normalizeAzureSubscriptionGuid(subscriptionSelection) ===
                azureSubscriptionId);
    };
    // Reactive check for every required field up to (but excluding) Products.
    // IcM and SR requirements vary by the selected call type.
    const isPreProductComplete = () => isCustomerComplete() &&
        AZURE_FLOW_CALL_TYPES.has(selectedCallType) &&
        (!callTypeRequiresIncident(selectedCallType) ||
            selectedIncidents.length > 0) &&
        (!callTypeRequiresSupportRequest(selectedCallType) ||
            selectedSupportRequestNumbers.length > 0) &&
        isSubscriptionComplete() &&
        isContextSectionComplete();
    // Handle Next button click in Recent Outages section - calls agents automatically
    const handleOutagesNext = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        // Immediately proceed to next section (non-blocking)
        setIsOutagesSectionExpanded(false);
        setIsContextSectionExpanded(true);
        // Unlock the context and product component sections
        setSectionsUnlocked((prev) => (Object.assign(Object.assign({}, prev), { context: true, productComponent: true })));
    }), []);
    // Handle Continue button click in Context & Rationale section
    const handleContextContinue = () => __awaiter(void 0, void 0, void 0, function* () {
        if (isPreProductComplete()) {
            // Collapse current sections and expand Product Component
            setIsContextSectionExpanded(false);
            setIsOutagesSectionExpanded(false);
            setIsProductComponentExpanded(true);
            // Unlock product component section
            setSectionsUnlocked((prev) => (Object.assign(Object.assign({}, prev), { productComponent: true })));
        }
    });
    // Validation logic for Products section: the primary row (row 0) must hold a
    // CURRENT RM2 catalogue product. A stale or non-catalogue value, or any value
    // while the catalogue is unavailable, must not satisfy completion; otherwise
    // Review & Submit could send a product name RM2 rejects. Requiring row 0
    // (not any row) keeps the "Primary product" field the submitted primary,
    // since buildSelectedProducts drops blank rows. AB#39926822.
    const isProductComponentSectionComplete = () => { var _a, _b; return azureComponentOptionKeys.has((_b = (_a = customProductRows[0]) === null || _a === void 0 ? void 0 : _a.value.trim()) !== null && _b !== void 0 ? _b : ""); };
    // Reactive check for the entire form (all 7 required fields), including Products.
    // Gates the "Review & Create Call" button so it is enabled only when every
    // required field is satisfied.
    const isFormComplete = () => isPreProductComplete() && isProductComponentSectionComplete();
    const validateTab = () => {
        // Defense in depth: the external wizard "Next" path must not bypass the
        // reactive required-field gating and advance an incomplete form.
        if (!isFormComplete()) {
            return;
        }
        if (isManualCustomerEntry && customerCountry.trim() === "") {
            setValidCustomerCountry(false);
            return;
        }
        // No longer requiring call type selection
        setTimeout(() => {
            if (moveToNextTab) {
                moveToNextTab(isValid, {
                    type: selectedCallType,
                    typeKey: selectedCallType,
                    customerTpid: customerTpid,
                    customerName: customerName,
                    customerCountry: customerCountry,
                    customerCountryId,
                    customerCountryAlpha2,
                    customerCountryAlpha3,
                    customerCountryIsEU,
                    customerMsSalesId,
                    customerSegment,
                    isManualCustomerEntry: isManualCustomerEntry,
                    selectedIncidents,
                    selectedIncidentOutageMap,
                    selectedSupportRequestNumbers,
                    supportTicketTitleMap,
                    azureSubscriptionId,
                    azureSubscriptionSelection: subscriptionSelection,
                    manualAzureSubscriptionId,
                    subscriptionCandidates,
                    subscriptionContextKey,
                });
            }
        }, 100);
    };
    const handleTileClick = (typeKey) => {
        if (selectedCallType && selectedCallType !== typeKey) {
            setSelectedIncidents([]);
            setSelectedSupportRequestNumbers([]);
        }
        // Set the selected call type
        setSelectedCallType(typeKey);
        // Check if "outage" was clicked and customer context is ready.
        // Search mode requires TPID+name, manual mode requires name+country.
        const hasSearchCustomer = isValidCustomerTpid(customerTpid) &&
            customerName.trim() !== "" &&
            (isSalesIdSelectionSatisfied || shouldPreserveRestoredSalesIdLayout);
        const hasManualCustomer = isManualCustomerEntry &&
            customerName.trim() !== "" &&
            customerCountry.trim() !== "";
        if (AZURE_FLOW_CALL_TYPES.has(typeKey) &&
            (hasSearchCustomer || hasManualCustomer)) {
            setShowOutagesSection(true);
        }
        else {
            setShowOutagesSection(false);
        }
        // Only unlock the outages section when call type is selected
        // Context and productComponent remain locked until Next is clicked
        setSectionsUnlocked((prev) => (Object.assign(Object.assign({}, prev), { outages: AZURE_FLOW_CALL_TYPES.has(typeKey) })));
    };
    return (_jsxs(Stack, Object.assign({ tokens: stackTokens.section, styles: mainContainerStyle }, { children: [_jsx(CustomerInformationSection, { customerTpid: customerTpid, setCustomerTpid: setCustomerTpid, customerName: customerName, setCustomerName: setCustomerName, customerCountry: customerCountry, setCustomerCountry: setCustomerCountry, customerCountryAlpha3: customerCountryAlpha3, isManualCustomerEntry: isManualCustomerEntry, setIsManualCustomerEntry: setIsManualCustomerEntry, validCustomerTpid: validCustomerTpid, setValidCustomerTpid: setValidCustomerTpid, validCustomerName: validCustomerName, setValidCustomerName: setValidCustomerName, validCustomerCountry: validCustomerCountry, setValidCustomerCountry: setValidCustomerCountry, isLoadingCustomer: isLoadingCustomer, setIsLoadingCustomer: setIsLoadingCustomer, customerMsSalesId: customerMsSalesId, salesIdCandidates: salesIdCandidates, salesIdLookupStatus: salesIdLookupStatus, setCustomerMsSalesId: setCustomerMsSalesId, retrySalesIdLookup: loadSalesIdCandidates, onCustomerDataLoaded: (loadedTpid, loadedCustomerName, loadedCountry, loadedCountryId, loadedCountryAlpha2, loadedCountryAlpha3, loadedCountryIsEU, _loadedMsSalesId, loadedSegment) => {
                    setCustomerTpid(loadedTpid);
                    setCustomerName(loadedCustomerName);
                    setCustomerCountry(loadedCountry || "");
                    setCustomerCountryId(loadedCountryId || "");
                    setCustomerCountryAlpha2(loadedCountryAlpha2 || "");
                    setCustomerCountryAlpha3(loadedCountryAlpha3 || "");
                    setCustomerCountryIsEU(loadedCountryIsEU || false);
                    setCustomerSegment(loadedSegment || "");
                    if (!loadedCountry) {
                        setValidCustomerCountry(undefined);
                    }
                    if (onCustomerDataLoaded) {
                        onCustomerDataLoaded(loadedTpid, loadedCustomerName, loadedCountry, loadedCountryId, loadedCountryAlpha2, loadedCountryAlpha3, loadedCountryIsEU, undefined, loadedSegment);
                    }
                } }), _jsx(CallTypeSection, { selectedCallType: selectedCallType, isCallTypeSectionExpanded: isCallTypeSectionExpanded, setIsCallTypeSectionExpanded: setIsCallTypeSectionExpanded, sectionsUnlocked: sectionsUnlocked, setHasManuallyReopenedCallType: setHasManuallyReopenedCallType, onTileClick: handleTileClick }), _jsx(RecentOutagesSection, Object.assign({ isExpanded: isOutagesSectionExpanded, setIsExpanded: setIsOutagesSectionExpanded, isUnlocked: sectionsUnlocked.outages, setHasManuallyReopened: setHasManuallyReopenedCallType, isManualCustomerEntry: isManualCustomerEntry, validCustomerTpid: validCustomerTpid, validCustomerName: validCustomerName, incidentCount: incidentCount, customerTpid: customerTpid, selectedCallType: selectedCallType, showOutagesSection: showOutagesSection, urlOutageIds: urlOutageIds, selectedIncidents: selectedIncidents, setSelectedIncidents: setSelectedIncidents, selectedSupportRequestNumbers: selectedSupportRequestNumbers, setSelectedSupportRequestNumbers: setSelectedSupportRequestNumbers, setIncidentCount: setIncidentCount, onNext: handleOutagesNext, isSubscriptionComplete: isSubscriptionComplete(), onIncidentsLoaded: setIncidentDetails, onSupportTicketsLoaded: setSupportTicketDetails }, { children: selectedCallType === "outage" && selectedIncidents.length > 0 && (_jsx(AzureSubscriptionSelector, { candidates: subscriptionCandidates, selectedOption: subscriptionSelection, manualValue: manualAzureSubscriptionId, lookupStatus: subscriptionLookupStatus, onSelectionChange: (selectedOption) => {
                        var _a, _b;
                        setSubscriptionSelection(selectedOption);
                        if (selectedOption === OTHER_SUBSCRIPTION_OPTION) {
                            setAzureSubscriptionId((_a = normalizeAzureSubscriptionGuid(manualAzureSubscriptionId)) !== null && _a !== void 0 ? _a : "");
                            return;
                        }
                        setManualAzureSubscriptionId("");
                        setAzureSubscriptionId((_b = normalizeAzureSubscriptionGuid(selectedOption)) !== null && _b !== void 0 ? _b : "");
                    }, onManualValueChange: (value) => {
                        var _a;
                        setManualAzureSubscriptionId(value);
                        setAzureSubscriptionId((_a = normalizeAzureSubscriptionGuid(value)) !== null && _a !== void 0 ? _a : "");
                    }, onRetry: () => {
                        void loadSubscriptionCandidates(true);
                    } })) })), _jsx(ContextRationaleSection, { isExpanded: isContextSectionExpanded, setIsExpanded: setIsContextSectionExpanded, isUnlocked: sectionsUnlocked.context, setHasManuallyReopened: setHasManuallyReopenedCallType, desiredOutcome: desiredOutcome, setDesiredOutcome: setDesiredOutcome, setIsDesiredOutcomeSelectionValid: setIsDesiredOutcomeSelectionValid, selectedTechnicalDepth: selectedTechnicalDepth, setSelectedTechnicalDepth: setSelectedTechnicalDepth, escalationReasonText: escalationReasonText, setEscalationReasonText: setEscalationReasonText, isOptionalSectionExpanded: isOptionalSectionExpanded, setIsOptionalSectionExpanded: setIsOptionalSectionExpanded, businessImpact: businessImpact, setBusinessImpact: setBusinessImpact, stakeholders: stakeholders, setStakeholders: setStakeholders, handleContextContinue: handleContextContinue, isContextSectionComplete: isContextSectionComplete, isPreProductComplete: isPreProductComplete }), _jsx(ProductComponentsSection, { isExpanded: isProductComponentExpanded, setIsExpanded: setIsProductComponentExpanded, isUnlocked: sectionsUnlocked.productComponent &&
                    (shouldPreserveRestoredSalesIdLayout || isPreProductComplete()), isProductComponentSectionComplete: isProductComponentSectionComplete, isFormComplete: isFormComplete, productRows: customProductRows, setProductRows: setCustomProductRows, azureComponentOptions: azureComponentOptions, azureComponentOptionsLoading: azureComponentOptionsLoading, azureComponentOptionsError: azureComponentOptionsError, onRetryAzureComponentOptions: getCommercialMetadata, customerTpid: customerTpid, customerName: customerName, customerCountry: customerCountry, customerCountryId: customerCountryId, customerCountryAlpha2: customerCountryAlpha2, customerCountryAlpha3: customerCountryAlpha3, customerCountryIsEU: customerCountryIsEU, customerMsSalesId: customerMsSalesId, customerSegment: customerSegment, isManualCustomerEntry: isManualCustomerEntry, selectedIncidents: selectedIncidents, selectedIncidentOutageMap: selectedIncidentOutageMap, selectedSupportRequestNumbers: selectedSupportRequestNumbers, supportTicketTitleMap: supportTicketTitleMap, azureSubscriptionId: azureSubscriptionId, azureSubscriptionSelection: subscriptionSelection, manualAzureSubscriptionId: manualAzureSubscriptionId, subscriptionCandidates: subscriptionCandidates, subscriptionContextKey: subscriptionContextKey, selectedCallType: selectedCallType, selectedTechnicalDepth: selectedTechnicalDepth, escalationReasons: escalationReasonText.trim() ? [escalationReasonText.trim()] : [], desiredOutcome: desiredOutcome, businessImpact: businessImpact, stakeholders: stakeholders, escalationReasonSummary: escalationReasonText, moveToNextTab: moveToNextTab })] })));
};
export default ConfidenceCallTypeTab;
